
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    User,
    BookOpen,
    GraduationCap,
    ClipboardCheck,
    BarChart3,
    MessageSquare,
    Send,
    Loader2,
    FileText,
    Plus,
    Trash2,
    CheckSquare,
    Info,
    Save,
    Download,
    History,
    Clock,
    ChevronRight,
    Eye,
    ArrowLeft,
    Share2,
    Edit3,
    Trophy,
    Star
} from 'lucide-react';

import {
    collection,
    addDoc,
    onSnapshot,
    query,
    serverTimestamp,
    doc,
    deleteDoc,
    getDoc,
    setDoc
} from 'firebase/firestore';
import { fetchWithGeminiResilience } from './geminiClient';

// Matriz de Competências Detalhada
const DETAILED_CRITERIA = {
    "Domínio do Conteúdo": {
        0: "Desconhecimento de conceitos básicos.",
        1: "Erros conceituais frequentes.",
        2: "Domínio apenas básico/superficial.",
        3: "Domínio satisfatório do currículo.",
        4: "Domínio sólido e atualizado.",
        5: "Autoridade absoluta e transdisciplinar."
    },
    "Metodologia de Ensino": {
        0: "Ausência de estratégia didática.",
        1: "Metodologia monótona e única.",
        2: "Tentativas de variação sem clareza.",
        3: "Métodos adequados e eficazes.",
        4: "Estratégias diversificadas e críticas.",
        5: "Inovação total e promoção de autonomia."
    },
    "Relacionamento com Alunos": {
        0: "Postura autoritária ou omissa.",
        1: "Dificuldade em manter respeito.",
        2: "Relacionamento funcional mas distante.",
        3: "Clima de cordialidade e respeito.",
        4: "Confiança e mediação eficaz.",
        5: "Liderança inspiradora e acolhedora."
    },
    "Pontualidade e Assiduidade": {
        0: "Atrasos e faltas constantes.",
        1: "Atrasos frequentes.",
        2: "Assíduo mas com atrasos em intervalos.",
        3: "Pontual e cumpre rigorosamente.",
        4: "Demonstra prontidão constante.",
        5: "Exemplo absoluto de compromisso."
    },
    "Uso de Tecnologias": {
        0: "Resistência total ao uso digital.",
        1: "Uso forçado com grande dificuldade.",
        2: "Utiliza recursos básicos (slides).",
        3: "Uso funcional e regular das ferramentas.",
        4: "Integração criativa e potencializada.",
        5: "Domínio avançado e orgânico."
    },
    "Plano de Aula": {
        0: "Não planeja ou ignora cronograma.",
        1: "Plano vago e raramente concluído.",
        2: "Segue parcialmente o planejamento.",
        3: "Cumpre prazos e objetivos previstos.",
        4: "Execução excelente com ajustes ágeis.",
        5: "Gestão impecável e antecipada."
    },
    "Engajamento": {
        0: "Alunos apáticos e sem incentivo.",
        1: "Baixo estímulo à interação.",
        2: "Estimula apenas pequenos grupos.",
        3: "Promove participação ativa geral.",
        4: "Alto nível de entusiasmo na turma.",
        5: "Alunos protagonistas e imersos."
    },
    "Clareza na Comunicação": {
        0: "Comunicação confusa ou inadequada.",
        1: "Dificuldade em explicar o simples.",
        2: "Comunicação básica com ruídos.",
        3: "Clara, objetiva e adequada.",
        4: "Excelente oratória e didática verbal.",
        5: "Magistral; envolvente e perfeita."
    },
    "Postura e Vestimenta": {
        0: "Antiprofissional/Inadequada.",
        1: "Descumpre código de ética.",
        2: "Oscilante ou excessivamente casual.",
        3: "Profissional e adequado às normas.",
        4: "Exemplar; transmite autoridade.",
        5: "Referência de elegância e ética."
    }
};

const CRITERIA_LIST = Object.keys(DETAILED_CRITERIA);
const SERIES_OPTIONS = ["6º Ano", "7º Ano", "8º Ano", "9º Ano", "1ª Série EM", "2ª Série EM", "3ª Série EM"];
const SUBJECT_OPTIONS = ["Ciências da Natureza", "Matemática", "História", "Geografia", "Língua Portuguesa", "Língua Espanhola", "Língua Inglesa", "PLIPT", "Arte", "Educação Física", "Produção Textual", "Biologia", "Química", "Física", "Sociologia", "Filosofia", "Literatura"];
const QUARTERS = ["1º Trimestre", "2º Trimestre", "3º Trimestre"];
const LOGO_URL = "https://i.postimg.cc/Vs77fcRR/Logo_Escola_Eleva_Barra_Horizontal_Inspired.png";
const API_KEY = "AQ.Ab8RN6L9xqG2T6VdoL7ChC1S_aV8DS1a1UWdXhScIx9CHlwsHQ";

const TeacherEvaluation = ({ db, auth, currentUser, appId }) => {
    const [user, setUser] = useState(currentUser || null);
    const [savedEvaluations, setSavedEvaluations] = useState([]);
    const [teacherName, setTeacherName] = useState('');
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [selectedGrades, setSelectedGrades] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState(QUARTERS[0]);
    const [scores, setScores] = useState(CRITERIA_LIST.reduce((acc, curr) => ({ ...acc, [curr]: 0 }), {}));
    const [evalDate, setEvalDate] = useState(new Date().toISOString().split('T')[0]);
    const [obs1, setObs1] = useState('');
    const [obs2, setObs2] = useState('');
    const [aiReport, setAiReport] = useState('');
    const [isLoadingAi, setIsLoadingAi] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [viewMode, setViewMode] = useState('form'); // 'form', 'history', 'report'
    const [currentReport, setCurrentReport] = useState(null);
    const [editingDocId, setEditingDocId] = useState(null);

    // Deriva lista única de professores para o autocomplete
    const uniqueTeachers = useMemo(() => {
        const names = savedEvaluations.map(e => e.teacherName).filter(Boolean);
        return [...new Set(names)].sort();
    }, [savedEvaluations]);


    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Inicialização
    useEffect(() => {
        if (currentUser) setUser(currentUser);
    }, [currentUser]);



    useEffect(() => {
        if (!user || !db) return;
        const q = collection(db, 'artifacts', appId, 'public', 'data', 'evaluations');
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const evals = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSavedEvaluations(evals.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)));
        });
        return () => unsubscribe();
    }, [user, db, appId]);

    // Carregar Scripts Externos (Chart.js e jsPDF)
    useEffect(() => {
        const loadScripts = async () => {
            const scripts = [
                "https://cdn.jsdelivr.net/npm/chart.js",
                "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"
            ];
            for (const src of scripts) {
                if (!document.querySelector(`script[src="${src}"]`)) {
                    const script = document.createElement("script");
                    script.src = src;
                    script.async = false;
                    document.body.appendChild(script);
                }
            }
        };
        loadScripts();
    }, []);

    // Atualizar Gráfico de Teia
    useEffect(() => {
        if (!window.Chart || !chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');
        if (chartInstance.current) chartInstance.current.destroy();

        chartInstance.current = new window.Chart(ctx, {
            type: 'radar',
            data: {
                labels: CRITERIA_LIST.map(l => l.length > 15 ? l.substring(0, 15) + '...' : l),
                datasets: [{
                    label: 'Pontuação',
                    data: CRITERIA_LIST.map(c => scores[c]),
                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    borderColor: 'rgb(59, 130, 246)',
                    pointBackgroundColor: 'rgb(59, 130, 246)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgb(59, 130, 246)'
                }]
            },
            options: {
                elements: { line: { borderWidth: 3 } },
                scales: {
                    r: {
                        angleLines: { display: true, color: 'rgba(255,255,255,0.2)' },
                        grid: { color: 'rgba(255,255,255,0.2)' },
                        suggestedMin: 0,
                        suggestedMax: 5,
                        ticks: { stepSize: 1, display: false, backdropColor: 'transparent' },
                        pointLabels: { color: '#94a3b8', font: { size: 10 } }
                    }
                },
                plugins: { legend: { display: false } }
            }
        });
    }, [scores, showHistory, viewMode]);

    const average = useMemo(() => {
        const total = Object.values(scores).reduce((a, b) => a + b, 0);
        return (total / CRITERIA_LIST.length).toFixed(2);
    }, [scores]);

    const performance = useMemo(() => {
        const n = parseFloat(average);
        if (n < 2) return { label: "Inadequado", color: "text-rose-600", bg: "bg-rose-50" };
        if (n < 3.5) return { label: "Desenvolvimento Necessário", color: "text-amber-600", bg: "bg-amber-50" };
        if (n < 4.5) return { label: "Atende Plenamente", color: "text-blue-600", bg: "bg-blue-50" };
        return { label: "Excede Expectativas", color: "text-emerald-600", bg: "bg-emerald-50" };
    }, [average]);

    const handleGradeToggle = (grade) => {
        setSelectedGrades(prev => prev.includes(grade) ? prev.filter(g => g !== grade) : [...prev, grade]);
    };

    const handleSubjectToggle = (subject) => {
        setSelectedSubjects(prev => prev.includes(subject) ? prev.filter(s => s !== subject) : [...prev, subject]);
    };

    const handleScoreChange = (criterion, val) => {
        setScores(prev => ({ ...prev, [criterion]: parseInt(val) }));
    };

    // Componente Helper para formatar texto da IA na Tela
    const FormattedText = ({ text }) => {
        if (!text) return null;
        return (
            <div className="space-y-3 text-slate-300">
                {text.split('\n').map((line, i) => {
                    const clean = line.replace(/\*\*/g, '').replace(/##/g, '').trim();
                    if (!clean) return <div key={i} className="h-2" />;

                    if (line.startsWith('##')) {
                        return <h4 key={i} className="text-blue-400 font-bold text-lg mt-4 mb-2 border-b border-blue-500/30 pb-1">{clean}</h4>;
                    }
                    if (line.trim().startsWith('*') || line.trim().startsWith('-')) {
                        return (
                            <div key={i} className="flex gap-2 items-start pl-2">
                                <span className="text-blue-500 mt-1.5">•</span>
                                <span className="flex-1 leading-relaxed">{clean.replace(/^[\*\-]\s*/, '')}</span>
                            </div>
                        );
                    }
                    // Bold detection for inline text
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    return (
                        <p key={i} className="leading-relaxed">
                            {parts.map((part, j) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                    return <strong key={j} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
                                }
                                return part;
                            })}
                        </p>
                    );
                })}
            </div>
        );
    };

    const generatePDF = async (data = null) => {
        if (!data && !teacherName) {
            alert("Por favor, preencha o Nome do Professor antes de gerar o PDF.");
            return;
        }
        if (!window.jspdf) {
            alert("A biblioteca de PDF ainda está sendo carregada. Aguarde alguns segundos e tente novamente.");
            return;
        }
        const target = data || { teacherName, evalDate, subjects: selectedSubjects, grades: selectedGrades, quarter: selectedQuarter, scores, average, performance: performance.label, obsCampo: obs1, obsGeral: obs2, aiReport };
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        let cursorY = 20;

        // Fundo Escuro Global (Pagina 1)
        doc.setFillColor(30, 58, 95); // Navy Palette
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Helper para Adicionar Nova Página com Fundo
        const checkAddPage = (heightNeeded) => {
            if (cursorY + heightNeeded > pageHeight - margin) {
                doc.addPage();
                doc.setFillColor(30, 58, 95);
                doc.rect(0, 0, pageWidth, pageHeight, 'F');
                cursorY = 20;
                return true;
            }
            return false;
        };

        // Helper para carregar imagem
        const loadImage = (url) => new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = url;
        });

        const drawCard = (y, height, title) => {
            doc.setFillColor(41, 74, 115); // Lighter Navy
            doc.setDrawColor(51, 65, 85); // slate-700
            doc.roundedRect(margin, y, pageWidth - (margin * 2), height, 3, 3, 'FD');

            // Badge Icon Title
            doc.setFillColor(149, 182, 223); // Sky Blue
            doc.roundedRect(margin - 4, y + 6, 8, 20, 2, 2, 'F'); // strip lateral

            if (title) {
                doc.setFontSize(14);
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.text(title.toUpperCase(), margin + 10, y + 12);
            }
        };

        try {
            // Header
            const logoImg = await loadImage(LOGO_URL);
            // Logo Background pill
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(margin, cursorY, 35, 14, 2, 2, 'F');
            if (logoImg) doc.addImage(logoImg, 'PNG', margin + 2, cursorY + 1, 31, 12);

            doc.setFontSize(10);
            doc.setTextColor(148, 163, 184); // slate-400
            doc.text("SISTEMA DE AVALIAÇÃO DOCENTE", pageWidth - margin, cursorY + 8, { align: 'right' });
            cursorY += 25;

            // Titulo Principal
            doc.setFontSize(28);
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.text(target.teacherName, margin, cursorY);
            cursorY += 10;

            doc.setFontSize(12);
            doc.setTextColor(149, 182, 223); // Sky Blue
            // Usa a data de avaliação salva/selecionada ou a data atual como fallback
            const displayDate = target.evalDate ? new Date(target.evalDate).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR');
            doc.text(`${target.quarter} • Avaliado em: ${displayDate}`, margin, cursorY);
            cursorY += 15;

            // Info Card
            drawCard(cursorY, 35, "");
            doc.setFontSize(10);
            doc.setTextColor(148, 163, 184); // slate-400
            doc.setFont("helvetica", "normal");
            doc.text("Disciplinas Ministradas:", margin + 10, cursorY + 12);
            doc.text("Segmentos de Atuação:", margin + 10, cursorY + 22);

            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.text(target.subjects.join(', '), margin + 55, cursorY + 12);
            doc.text(target.grades.join(', '), margin + 55, cursorY + 22);
            cursorY += 45;

            // Score Card (Destaque)
            doc.setFillColor(41, 74, 115); // Lighter Navy
            doc.roundedRect(margin, cursorY, pageWidth - (margin * 2), 40, 4, 4, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text("MÉDIA GERAL", margin + 15, cursorY + 15);

            doc.setFontSize(32);
            doc.setFont("helvetica", "bold");
            doc.text(target.average, margin + 15, cursorY + 28);

            doc.setFontSize(14);
            doc.text(target.performance.toUpperCase(), pageWidth - margin - 15, cursorY + 25, { align: 'right' });

            // Chart Preview
            if (chartRef.current && !data) {
                const chartImg = chartRef.current.toDataURL('image/png');
                const chartSize = 34;
                doc.addImage(chartImg, 'PNG', (pageWidth / 2) - (chartSize / 2), cursorY + 3, chartSize, chartSize);
            }
            cursorY += 50;

            // Competências
            checkAddPage(100);
            doc.setFontSize(16);
            doc.setTextColor(255, 255, 255);
            doc.text("Avaliação de Competências", margin, cursorY);
            cursorY += 10;

            Object.entries(target.scores).forEach(([crit, val]) => {
                checkAddPage(35);

                // Item Container
                doc.setDrawColor(51, 65, 85);
                doc.setFillColor(41, 74, 115);
                doc.roundedRect(margin, cursorY, pageWidth - (margin * 2), 22, 2, 2, 'FD');

                // Title & Score
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.text(crit, margin + 10, cursorY + 10);

                doc.setTextColor(149, 182, 223);
                doc.text(`${val}/5`, pageWidth - margin - 10, cursorY + 10, { align: 'right' });

                // Description
                doc.setFontSize(9);
                doc.setTextColor(148, 163, 184); // slate-400
                doc.setFont("helvetica", "normal");
                const desc = DETAILED_CRITERIA[crit][val];
                doc.text(desc, margin + 10, cursorY + 18);

                // Progress Bar Background
                doc.setFillColor(30, 58, 95);
                doc.roundedRect(margin + 10, cursorY + 20, pageWidth - (margin * 2) - 20, 1, 0.5, 0.5, 'F');
                // Progress Bar Fill
                doc.setFillColor(149, 182, 223);
                const fillWidth = ((pageWidth - (margin * 2) - 20) * (val / 5));
                doc.roundedRect(margin + 10, cursorY + 20, fillWidth, 1, 0.5, 0.5, 'F');

                cursorY += 28;
            });

            // IA Report
            if (target.aiReport) {
                checkAddPage(60);
                cursorY += 10;

                // Header IA
                doc.setFillColor(41, 74, 115);
                doc.roundedRect(margin, cursorY, pageWidth - (margin * 2), 12, 2, 2, 'F');
                doc.setTextColor(149, 182, 223); // Sky Blue
                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.text("ANÁLISE PEDAGÓGICA (IA)", margin + 5, cursorY + 8);
                cursorY += 20;

                doc.setTextColor(203, 213, 225); // slate-300
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);

                // Parse Text for PDF
                const lines = target.aiReport.split('\n');
                lines.forEach(line => {
                    const clean = line.replace(/\*\*/g, '').replace(/##/g, '').trim();
                    if (!clean) {
                        cursorY += 4;
                        return;
                    }

                    checkAddPage(15);

                    if (line.startsWith('##')) {
                        doc.setFont("helvetica", "bold");
                        doc.setTextColor(149, 182, 223); // Sky Blue
                        doc.setFontSize(11);
                        doc.text(clean.toUpperCase(), margin, cursorY);
                        cursorY += 6;
                    } else if (line.trim().startsWith('*') || line.trim().startsWith('-')) {
                        doc.setFont("helvetica", "normal");
                        doc.setTextColor(203, 213, 225);
                        doc.setFontSize(10);
                        const bulletText = clean.replace(/^[\*\-]\s*/, '');
                        const splitBullet = doc.splitTextToSize(`• ${bulletText}`, pageWidth - (margin * 2));
                        doc.text(splitBullet, margin, cursorY);
                        cursorY += (splitBullet.length * 5) + 2;
                    } else {
                        doc.setFont("helvetica", "normal");
                        doc.setTextColor(203, 213, 225);
                        doc.setFontSize(10);
                        const splitPara = doc.splitTextToSize(clean, pageWidth - (margin * 2));
                        doc.text(splitPara, margin, cursorY);
                        cursorY += (splitPara.length * 5) + 2;
                    }
                });
            }

            doc.save(`Avaliacao_MOPI_${target.teacherName.replace(/\s+/g, '_')}_Dark.pdf`);
        } catch (e) {
            console.error("PDF Error:", e);
        }
    };

    const generateAIReport = async () => {
        if (!teacherName) {
            alert("Por favor, preencha o Nome do Professor para gerar o parecer.");
            return;
        }
        if (!API_KEY) {
            setError("Chave de API do Gemini não configurada no Sistema. Contate a Coordenação.");
            return;
        }
        setIsLoadingAi(true);
        setError(null);
        const systemPrompt = `Você é um coordenador pedagógico do Colégio MOPI. Gere um relatório profissional e motivador. Baseie-se nas rubricas e observações fornecidas.`;
        const userPrompt = `
      Professor: ${teacherName}
      Trimestre: ${selectedQuarter}
      Disciplinas: ${selectedSubjects.join(', ')}
      Resultados das Rubricas: ${Object.entries(scores).map(([k, v]) => `${k}: ${v} (${DETAILED_CRITERIA[k][v]})`).join(', ')}
      Dados de Campo: ${obs1}
      Feedback Geral: ${obs2}
    `;
        try {
            const payload = {
                contents: [{ parts: [{ text: userPrompt }] }],
                systemInstruction: { parts: [{ text: systemPrompt }] }
            };
            const res = await fetchWithGeminiResilience(API_KEY, payload);

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(`API Error ${res.status}: ${errData.error?.message || res.statusText}`);
            }

            const data = await res.json();
            setAiReport(data.candidates?.[0]?.content?.parts?.[0]?.text || "Erro ao gerar relatório (Resposta vazia).");
        } catch (e) {
            console.error(e);
            setError(`Erro na conexão com a IA: ${e.message}`);
            alert(`Erro detalhado da IA: ${e.message}`);
        }
        finally { setIsLoadingAi(false); }
    };

    const saveEvaluation = async () => {
        if (!user || !teacherName) return;
        setIsSaving(true);
        try {
            const newDoc = {
                teacherName, evalDate, subjects: selectedSubjects, grades: selectedGrades, quarter: selectedQuarter,
                scores, average, performance: performance.label, obsCampo: obs1, obsGeral: obs2, aiReport,
                timestamp: serverTimestamp(), createdBy: user?.uid || 'anonymous'
            };

            if (editingDocId) {
                // UPDATE existing
                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'evaluations', editingDocId), newDoc, { merge: true });
                setCurrentReport({ ...newDoc, id: editingDocId });
                alert("Avaliação atualizada com sucesso!");
            } else {
                // CREATE new
                const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'evaluations'), newDoc);
                setCurrentReport({ ...newDoc, id: docRef.id });
                alert("Avaliação salva com sucesso!");
            }

            // Switch to report view
            setViewMode('report');
        } catch (err) {
            console.error(err);
            setError(`Erro ao salvar: ${err.message}`);
            alert(`Erro ao salvar no banco de dados: ${err.message}`);
        }
        finally { setIsSaving(false); }
    };


    // --- RENDERIZADORES DE VISUALIZAÇÃO ---

    const renderReportView = () => {
        if (!currentReport) return null;

        const handleEdit = () => {
            setTeacherName(currentReport.teacherName);
            setSelectedSubjects(currentReport.subjects || []);
            setSelectedGrades(currentReport.grades || []);
            setSelectedQuarter(currentReport.quarter);
            setScores(currentReport.scores);
            setObs1(currentReport.obsCampo || '');
            setObs2(currentReport.obsGeral || '');
            setAiReport(currentReport.aiReport || '');
            setObs2(currentReport.obsGeral || '');
            setAiReport(currentReport.aiReport || '');
            setEvalDate(currentReport.evalDate || new Date().toISOString().split('T')[0]);
            setEditingDocId(currentReport.id); // Set ID for editing
            setViewMode('form');
        };

        return (
            <div className="min-h-screen bg-slate-900 text-white font-sans selection:bg-blue-500 selection:text-white pb-20">
                {/* Header Dark */}
                <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setViewMode(showHistory ? 'history' : 'form')} className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white">
                            <ArrowLeft size={24} />
                        </button>
                        <h1 className="text-lg font-bold">Relatório Detalhado</h1>
                    </div>
                    <button className="p-2 hover:bg-slate-800 rounded-full transition-colors text-blue-400">
                        <Share2 size={24} />
                    </button>
                </header>

                <div className="max-w-3xl mx-auto p-6 space-y-8 animate-in slide-in-from-bottom-4 duration-500">

                    {/* Profile Section */}
                    <div className="text-center space-y-4 pt-4">
                        <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 p-1 shadow-2xl shadow-blue-900/50 relative">
                            <img
                                src={`https://ui-avatars.com/api/?name=${currentReport.teacherName}&background=0f172a&color=fff&size=200`}
                                alt="Avatar"
                                className="w-full h-full rounded-full object-cover border-4 border-slate-900"
                            />
                            <div className="absolute bottom-0 right-0 bg-blue-500 p-1.5 rounded-full border-4 border-slate-900">
                                <GraduationCap size={14} className="text-white" />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold tracking-tight">{currentReport.teacherName}</h2>
                            <p className="text-slate-400 text-sm mt-1 font-medium">{currentReport.subjects?.join(' • ')}</p>
                            <p className="text-slate-500 text-xs">{currentReport.grades?.join(', ')}</p>
                        </div>
                        <div className="flex justify-center gap-3">
                            <span className="px-3 py-1 bg-slate-800 rounded-full text-xs font-semibold text-blue-400 border border-slate-700">{currentReport.quarter}</span>
                            <span className="px-3 py-1 bg-slate-800 rounded-full text-xs font-semibold text-indigo-400 border border-slate-700">Avaliado em: {currentReport.evalDate ? new Date(currentReport.evalDate).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')}</span>
                        </div>
                    </div>

                    {/* Overall Score Card */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-8 shadow-2xl shadow-blue-900/30 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-10 opacity-10 transform translate-x-10 -translate-y-10">
                            <Trophy size={150} />
                        </div>
                        <div className="relative z-10 flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-xs font-bold uppercase tracking-wider mb-1">Média Geral</p>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-6xl font-black tracking-tighter">{currentReport.average}</span>
                                    <span className="text-2xl text-blue-200 font-medium">/ 5.0</span>
                                </div>
                                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs font-bold border border-white/10">
                                    {currentReport.performance}
                                </div>
                            </div>
                            <div className="w-16 h-16 rounded-full border-4 border-white/30 flex items-center justify-center bg-white/10 backdrop-blur-md shadow-lg">
                                <Trophy size={28} className="text-white drop-shadow-md" />
                            </div>
                        </div>
                    </div>

                    {/* Chart Preview (Collapsed) */}
                    <div className="bg-slate-800/50 rounded-3xl p-6 border border-slate-700/50">
                        <div className="flex items-center gap-3 mb-4">
                            <BarChart3 className="text-blue-500" size={20} />
                            <h3 className="font-bold text-slate-200">Radar de Competências</h3>
                        </div>
                        <div className="h-64 w-full flex justify-center">
                            <canvas ref={chartRef} />
                        </div>
                    </div>


                    {/* Competencies List */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 mb-2 px-1">
                            <BarChart3 className="text-blue-500" size={20} />
                            <h3 className="font-bold text-lg">Avaliação de Competências</h3>
                        </div>

                        {Object.entries(currentReport.scores).map(([crit, val]) => (
                            <div key={crit} className="bg-slate-800 rounded-2xl p-5 border border-slate-700/50 hover:bg-slate-800/80 transition-colors">
                                <div className="flex justify-between items-start mb-3">
                                    <h4 className="font-bold text-slate-200">{crit}</h4>
                                    <div className="flex items-center gap-1 bg-slate-900/50 px-2 py-1 rounded-lg border border-slate-700">
                                        <span className="text-blue-400 font-bold text-sm">{val}</span>
                                        <Star size={12} className="text-blue-400 fill-blue-400" />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400 leading-relaxed mb-4 min-h-[2.5em]">
                                    {DETAILED_CRITERIA[crit][val]}
                                </p>
                                {/* Progress Bar */}
                                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out"
                                        style={{ width: `${(val / 5) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* AI Analysis Section */}
                    {currentReport.aiReport && (
                        <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700/50">
                            <div className="flex items-center gap-3 mb-4 text-blue-400">
                                <FileText size={20} />
                                <h3 className="font-bold text-lg">Análise Pedagógica (IA)</h3>
                            </div>
                            <div className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed">
                                <FormattedText text={currentReport.aiReport} />
                            </div>
                        </div>
                    )}

                    {/* Footer Actions */}
                    <div className="fixed bottom-0 left-0 w-full bg-slate-900/90 backdrop-blur-xl border-t border-slate-800 p-4 pb-8 z-40">
                        <div className="max-w-3xl mx-auto flex gap-4">
                            <button
                                onClick={() => generatePDF(currentReport)}
                                className="flex-1 py-3.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold text-sm border border-slate-700 flex items-center justify-center gap-2 transition-all"
                            >
                                <Download size={18} />
                                Exportar PDF
                            </button>
                            <button
                                onClick={handleEdit}
                                className="flex-1 py-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2 transition-all"
                            >
                                <Edit3 size={18} />
                                Editar Avaliação
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (viewMode === 'report') {
        return renderReportView();
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 font-sans text-slate-800">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* Header Profissional */}
                <header className="bg-white p-6 md:p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <img src={LOGO_URL} alt="MOPI Logo" className="h-16 w-auto drop-shadow-sm" />
                        <div className="h-12 w-[2px] bg-slate-100 hidden md:block" />
                        <div>
                            <h1 className="text-2xl font-black text-[#1E293B] tracking-tight">Avaliação Docente</h1>
                            <p className="text-blue-600 text-xs font-bold uppercase tracking-[0.2em]">Painel de Gestão Pedagógica</p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                if (viewMode === 'history') setViewMode('form');
                                else {
                                    setShowHistory(true);
                                    setViewMode('history');
                                }
                            }}
                            className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black transition-all shadow-sm ${viewMode === 'history' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-white border border-slate-200'}`}
                        >
                            {viewMode === 'history' ? <Eye size={18} /> : <History size={18} />}
                            {viewMode === 'history' ? 'Voltar para Avaliação' : 'Histórico'}
                        </button>
                        {viewMode !== 'history' && (
                            <button
                                onClick={() => {
                                    setTeacherName('');
                                    setAiReport('');
                                    setEditingDocId(null);
                                    setScores(CRITERIA_LIST.reduce((acc, curr) => ({ ...acc, [curr]: 0 }), {}));
                                    setObs1('');
                                    setObs2('');
                                    setSelectedSubjects([]);
                                    setSelectedGrades([]);
                                }}
                                className="p-3 bg-slate-50 text-slate-400 hover:text-rose-500 rounded-2xl transition-colors border border-slate-100"
                                title="Limpar Formulário"
                            >
                                <Trash2 size={24} />
                            </button>
                        )}
                    </div>
                </header>

                {
                    viewMode === 'history' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
                            {savedEvaluations.length === 0 ? (
                                <div className="col-span-full py-40 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200">
                                    <Clock size={80} className="mx-auto mb-6 text-slate-100" />
                                    <p className="text-slate-400 font-bold text-lg tracking-tight">Nenhum relatório arquivado ainda.</p>
                                </div>
                            ) : (
                                savedEvaluations.map((ev) => (
                                    <div key={ev.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-lg shadow-slate-200/30 hover:-translate-y-2 transition-all group">
                                        <div className="flex justify-between items-start mb-6">
                                            <span className="text-[10px] font-black uppercase text-blue-600 px-3 py-1.5 bg-blue-50 rounded-xl">{ev.quarter}</span>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Tem certeza que deseja excluir esta avaliação permanentemente?')) {
                                                        deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'evaluations', ev.id));
                                                    }
                                                }}
                                                className="p-2 bg-rose-50 text-rose-500 hover:bg-rose-100 hover:scale-110 rounded-full transition-all"
                                                title="Excluir Avaliação"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                        <h3 className="font-black text-slate-900 text-2xl leading-tight mb-2">{ev.teacherName}</h3>
                                        <div className="space-y-1 mb-8">
                                            <p className="text-xs text-slate-400 font-bold truncate">{ev.subjects?.join(' • ')}</p>
                                            <p className="text-[10px] text-slate-300 font-medium">Séries: {ev.grades?.join(', ')}</p>
                                        </div>

                                        <div className="flex items-center justify-between border-t border-slate-50 pt-6">
                                            <div>
                                                <span className="text-4xl font-black text-slate-900 leading-none">{ev.average}</span>
                                                <p className="text-[9px] text-slate-400 font-black uppercase mt-1">Pontuação Final</p>
                                            </div>
                                            <button
                                                onClick={() => { setCurrentReport(ev); setViewMode('report'); }}
                                                className="bg-slate-900 text-white px-6 py-4 rounded-2xl hover:scale-105 transition-transform shadow-lg shadow-slate-200 text-xs font-bold flex items-center gap-2"
                                            >
                                                <Eye size={16} /> Ver
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                            <main className="lg:col-span-8 space-y-8">

                                {/* Card de Identificação */}
                                <section className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl shadow-slate-200/40 border border-slate-100">
                                    <div className="flex items-center gap-4 mb-10">
                                        <div className="p-4 bg-blue-50 text-blue-600 rounded-3xl"><User size={28} /></div>
                                        <h2 className="text-2xl font-black text-[#1E293B] tracking-tight uppercase">Docente & Disciplinas</h2>
                                    </div>

                                    <div className="space-y-10">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                            <div className="md:col-span-2 space-y-2">
                                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Nome Completo</label>
                                                <input
                                                    type="text"
                                                    list="teacher-suggestions"
                                                    className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-[1.5rem] focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-xl font-black placeholder:text-slate-200"
                                                    placeholder="Ex: Prof. Roberto"
                                                    value={teacherName}
                                                    onChange={(e) => setTeacherName(e.target.value)}
                                                />
                                                <datalist id="teacher-suggestions">
                                                    {uniqueTeachers.map(name => (
                                                        <option key={name} value={name} />
                                                    ))}
                                                </datalist>
                                            </div>
                                            <div className="md:col-span-3 lg:col-span-1 space-y-2">
                                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Data da Avaliação</label>
                                                <input
                                                    type="date"
                                                    className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-[1.5rem] focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-lg font-bold text-slate-700"
                                                    value={evalDate}
                                                    onChange={(e) => setEvalDate(e.target.value)}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Período</label>
                                                <select
                                                    className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-[1.5rem] focus:border-blue-500 focus:bg-white font-black text-lg cursor-pointer"
                                                    value={selectedQuarter}
                                                    onChange={(e) => setSelectedQuarter(e.target.value)}
                                                >
                                                    {QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2 flex items-center gap-2">
                                                <CheckSquare size={14} className="text-blue-500" /> Disciplinas Vinculadas
                                            </label>
                                            <div className="flex flex-wrap gap-2.5 p-6 bg-slate-50/50 rounded-[2rem] border border-slate-100">
                                                {SUBJECT_OPTIONS.map(subject => (
                                                    <button
                                                        key={subject}
                                                        onClick={() => handleSubjectToggle(subject)}
                                                        className={`px-5 py-2.5 rounded-xl text-xs font-black transition-all border-2 ${selectedSubjects.includes(subject)
                                                            ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-100'
                                                            : 'bg-white border-slate-100 text-slate-500 hover:border-blue-400 hover:text-blue-600'
                                                            }`}
                                                    >
                                                        {subject}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2 flex items-center gap-2">
                                                <GraduationCap size={14} className="text-slate-900" /> Séries / Segmentos
                                            </label>
                                            <div className="flex flex-wrap gap-2.5 p-6 bg-slate-50/50 rounded-[2rem] border border-slate-100">
                                                {SERIES_OPTIONS.map(grade => (
                                                    <button
                                                        key={grade}
                                                        onClick={() => handleGradeToggle(grade)}
                                                        className={`px-5 py-2.5 rounded-xl text-xs font-black transition-all border-2 ${selectedGrades.includes(grade)
                                                            ? 'bg-slate-900 border-slate-900 text-white'
                                                            : 'bg-white border-slate-100 text-slate-500 hover:border-slate-400'
                                                            }`}
                                                    >
                                                        {grade}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* Card de Critérios */}
                                <section className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl shadow-slate-200/40 border border-slate-100">
                                    <div className="flex items-center justify-between mb-12">
                                        <div className="flex items-center gap-4">
                                            <div className="p-4 bg-blue-50 text-blue-600 rounded-3xl"><BarChart3 size={28} /></div>
                                            <h2 className="text-2xl font-black text-[#1E293B] tracking-tight uppercase">Avaliação de Competências</h2>
                                        </div>
                                    </div>

                                    <div className="space-y-14">
                                        {CRITERIA_LIST.map(criterion => (
                                            <div key={criterion} className="relative group">
                                                <div className="flex justify-between items-center mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-2 h-8 bg-blue-600 rounded-full" />
                                                        <span className="text-lg font-black text-slate-800 tracking-tight">{criterion}</span>
                                                    </div>
                                                    <div className="bg-blue-600 px-5 py-1.5 rounded-full shadow-lg shadow-blue-100">
                                                        <span className="text-sm font-black text-white">{scores[criterion]} / 5</span>
                                                    </div>
                                                </div>

                                                <input
                                                    type="range"
                                                    min="0" max="5" step="1"
                                                    value={scores[criterion]}
                                                    onChange={(e) => handleScoreChange(criterion, e.target.value)}
                                                    className="w-full h-3 bg-slate-100 rounded-full appearance-none cursor-pointer accent-blue-600 mb-6"
                                                />

                                                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 flex gap-4 items-center">
                                                    <Info size={18} className="text-blue-400 flex-shrink-0" />
                                                    <p className="text-xs font-bold text-slate-600 leading-relaxed italic">
                                                        {DETAILED_CRITERIA[criterion][scores[criterion]]}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {/* Observações */}
                                <section className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl shadow-slate-200/40 border border-slate-100 space-y-10">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl"><MessageSquare size={20} /></div>
                                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Observações de Campo</h3>
                                        </div>
                                        <textarea
                                            className="w-full p-6 bg-slate-50 border-2 border-transparent rounded-[2rem] focus:border-blue-500 focus:bg-white focus:outline-none min-h-[160px] text-sm font-medium transition-all shadow-inner"
                                            placeholder="Dados objetivos coletados durante as observações em sala..."
                                            value={obs1}
                                            onChange={(e) => setObs1(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl"><Plus size={20} /></div>
                                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Feedback Complementar</h3>
                                        </div>
                                        <textarea
                                            className="w-full p-6 bg-slate-50 border-2 border-transparent rounded-[2rem] focus:border-blue-500 focus:bg-white focus:outline-none min-h-[160px] text-sm font-medium transition-all shadow-inner"
                                            placeholder="Impressões subjetivas e orientações gerais..."
                                            value={obs2}
                                            onChange={(e) => setObs2(e.target.value)}
                                        />
                                    </div>
                                </section>
                            </main>

                            {/* Sidebar Fixa */}
                            <aside className="lg:col-span-4 space-y-8">
                                <div className="sticky top-8 space-y-8">

                                    {/* Score & Radar Panel */}
                                    <div className={`p-10 rounded-[4rem] shadow-2xl border-4 border-white relative overflow-hidden transition-all duration-500 ${performance.bg}`}>
                                        <div className="text-center relative z-10 space-y-6">
                                            <p className="text-slate-400 font-black uppercase tracking-[0.3em] text-[10px]">Média de Performance</p>
                                            <div className="text-[9rem] font-black text-slate-900 leading-none tracking-tighter drop-shadow-sm">{average}</div>

                                            {/* Gráfico de Teia Inline */}
                                            <div className="bg-white p-6 rounded-[3rem] shadow-xl shadow-slate-200/50 mb-6">
                                                <canvas ref={chartRef} width="250" height="250" />
                                            </div>

                                            <div className={`text-xs font-black px-10 py-4 rounded-full inline-block border-2 border-white shadow-xl ${performance.color} bg-white uppercase tracking-widest`}>
                                                {performance.label}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Painel de Ações */}
                                    <div className="bg-white p-8 rounded-[3rem] shadow-xl border border-slate-100 space-y-4">
                                        <button
                                            onClick={generateAIReport}
                                            className={`w-full py-6 rounded-[2rem] font-black text-xs flex items-center justify-center gap-3 transition-all transform active:scale-95 shadow-2xl uppercase tracking-widest ${isLoadingAi
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                : 'bg-gradient-to-br from-indigo-600 to-blue-700 text-white hover:brightness-110 shadow-indigo-200'
                                                }`}
                                        >
                                            {isLoadingAi ? <Loader2 className="animate-spin" /> : <Send size={20} />}
                                            Processar Parecer IA
                                        </button>

                                        <div className="grid grid-cols-2 gap-4">
                                            <button
                                                onClick={saveEvaluation}
                                                className="py-5 bg-white border-2 border-slate-100 text-slate-900 rounded-[1.5rem] font-black text-[10px] flex items-center justify-center gap-2 hover:bg-slate-50 transition-all active:scale-95 uppercase tracking-widest"
                                            >
                                                {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                                                Salvar
                                            </button>
                                            <button
                                                onClick={() => generatePDF()}
                                                className="py-5 bg-slate-900 text-white rounded-[1.5rem] font-black text-[10px] flex items-center justify-center gap-2 hover:bg-slate-800 transition-all active:scale-95 shadow-lg shadow-slate-200 uppercase tracking-widest"
                                            >
                                                <Download size={16} />
                                                PDF
                                            </button>
                                        </div>
                                    </div>

                                    {/* IA Report Visual */}
                                    {aiReport && (
                                        <section className="bg-white p-10 rounded-[3.5rem] shadow-2xl border border-blue-50 animate-in zoom-in-95 duration-500 overflow-hidden relative">
                                            <div className="absolute top-0 right-0 p-8 opacity-5">
                                                <FileText size={120} className="text-blue-600" />
                                            </div>
                                            <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-50 relative z-10">
                                                <h3 className="text-[11px] font-black flex items-center gap-3 text-slate-900 uppercase tracking-widest">
                                                    <FileText size={18} className="text-blue-600" />
                                                    Análise Estratégica
                                                </h3>
                                                <button onClick={() => setAiReport('')} className="text-slate-300 hover:text-rose-500"><Trash2 size={16} /></button>
                                            </div>
                                            <div className="prose prose-slate prose-sm max-w-none text-slate-600 text-[14px] leading-relaxed max-h-[450px] overflow-y-auto pr-3 custom-scrollbar whitespace-pre-wrap font-medium relative z-10">
                                                {aiReport}
                                            </div>
                                        </section>
                                    )}

                                    {error && <div className="p-6 bg-rose-50 text-rose-600 text-[10px] font-black rounded-3xl border border-rose-100 uppercase tracking-widest text-center">{error}</div>}
                                </div>
                            </aside>
                        </div>
                    )
                }
            </div >

            <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
        input[type=range] { -webkit-appearance: none; }
        input[type=range]::-webkit-slider-thumb { 
          -webkit-appearance: none; 
          height: 32px; width: 32px; 
          border-radius: 50%; 
          background: #2563EB; 
          cursor: pointer; 
          border: 6px solid white; 
          box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.4);
        }
      `}</style>
        </div >
    );
};

export default TeacherEvaluation;
